import { forwardRef, InputHTMLAttributes, ReactNode, useId } from 'react'

import { cn } from '@/app/utils'
import MinMaxLengthMessage from '@/components/shared/input/ui/MinMaxLengthMessage'

import { useMinMaxLengthMessage } from '../../input/hooks/useMinMaxLengthMessage'
import AppInputMessage from '../../input/ui/AppInputMessage'

export type Props = InputHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean
  invalidMessage?: ReactNode
  inputClassName?: string
  borderColor?: string
  maxLengthMessageFrom?: number
  counterMessageColor?: string
  counterTestId?: string
  dataTestId?: string
}

const AppTextarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      className,
      invalid,
      invalidMessage,
      inputClassName,
      placeholder,
      minLength,
      maxLength,
      borderColor,
      maxLengthMessageFrom,
      onFocus,
      onBlur,
      counterMessageColor,
      counterTestId,
      dataTestId,
      ...props
    },
    ref
  ) => {
    const _id = useId()

    const {
      valueLength,
      isMinLengthInvalid,
      showMinLengthMessageValid,
      showMaxLengthMessageValid,
      setInputFocused
    } = useMinMaxLengthMessage({
      value: props.value,
      minLength,
      maxLengthMessageFrom
    })

    const currentBorderColor =
      invalid || isMinLengthInvalid
        ? 'border-red-80'
        : borderColor || 'border-transparent'

    return (
      <>
        <div
          className={cn(
            'relative flex h-[120px] cursor-text items-end overflow-hidden rounded-12 border bg-white font-pro',
            currentBorderColor,
            className
          )}
        >
          <textarea
            data-testid={dataTestId}
            id={_id}
            ref={ref}
            className={cn(
              'peer flex-grow',
              'h-[105px] resize-none px-12 pb-5 text-deepBlack outline-none transition text-16 md:pb-2',
              'pt-10 placeholder:opacity-0',
              inputClassName
            )}
            placeholder={placeholder}
            minLength={minLength}
            maxLength={maxLength}
            onFocus={event => {
              setInputFocused(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              setInputFocused(false)
              onBlur?.(event)
            }}
            {...props}
          />
          <label
            htmlFor={_id}
            className={cn(
              'absolute left-12 top-8 w-full translate-y-0 bg-white !text-black/50 transition-all text-12',
              'peer-placeholder-shown:top-16 peer-placeholder-shown:text-16',
              'pointer-events-none peer-focus:top-8 peer-focus:translate-y-0 peer-focus:text-12',
              'peer-[:not(:placeholder-shown)]:top-8'
            )}
          >
            {placeholder}
          </label>
        </div>
        {invalid ? (
          <AppInputMessage message={invalidMessage} />
        ) : (
          <>
            {Boolean(minLength) && (
              <MinMaxLengthMessage
                valueLength={valueLength}
                minLength={minLength}
                isLengthInvalid={isMinLengthInvalid}
                showMessageValid={showMinLengthMessageValid}
              />
            )}
            {Boolean(maxLength) && (
              <MinMaxLengthMessage
                dataTestId={counterTestId}
                valueLength={valueLength}
                maxLength={maxLength}
                showMessageValid={showMaxLengthMessageValid}
                textColor={counterMessageColor}
              />
            )}
          </>
        )}
      </>
    )
  }
)

AppTextarea.displayName = 'AppTextarea'
export default AppTextarea
